import _merge from "lodash/merge";
import React from "react";
import ReactDOMServer from "react-dom/server";
import merge from "deepmerge";
import { toast } from "react-toastify";
import { v4 } from "uuid";

import HelpDeskMessage from "../../help-desk/helpDeskMessage";
import { logger } from "../../services/loggingService";
import * as keys from "../../constants/localStorageKeys";
import OQstrings from "./strings";
import states from "../../data/insuredStates.json";
import { getCoverageGroupOrder } from "./constants";
import { searchLocaleByZipCodeV2 } from "../../services/onlineQuotingService";
import { toTitleCase } from "../../components/Factory";
import { Buffer } from "buffer";
import { formatDate } from "@ufginsurance/ui-kit";

export const inputTypes = [
  "checkbox",
  "date",
  "email",
  "file",
  "hidden",
  "password",
  "radio",
  "range",
  "tel",
  "text",
  "time",
  "url",
  "select",
  "switch",
  "togglelink",
  "data",
  "scheduleItemContact",
  "textArea",
  "dropdownToTextboxes",
  ""
];

export const _get = (obj, path, defValue) => {
  // If path is not defined or it has false value
  if (!path) return undefined;
  // Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
  // Regex explained: https://regexr.com/58j0k
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);
  // Find value
  const result = pathArray.reduce(
    (prevObj, key) => prevObj && prevObj[key],
    obj
  );
  // If found value is undefined return default value; otherwise return the value
  return result === undefined ? defValue : result;
};

export const _set = (obj, path, value) => {
  // Regex explained: https://regexr.com/58j0k
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);

  console.log({ pathArray });
  pathArray.reduce((acc, key, i) => {
    if (acc[key] === undefined) acc[key] = {};
    if (i === pathArray.length - 1) acc[key] = value;
    return acc[key];
  }, obj);
};

export const slugify = str => {
  if (!str) return "";
  return str.replace(/[^a-zA-Z0-9]/g, "_");
};

export const isUndefined = value => {
  return (
    !value ||
    typeof value === "undefined" ||
    value === "" ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" && Object.keys(value).length === 0)
  );
};

export const isDefined = value => !isUndefined(value);

export const getInputType = field => {
  if (isUndefined(field)) return "";

  if (!field.hasOwnProperty("dataType") && !field.hasOwnProperty("component"))
    return "";

  if (field.dataType === "dropdownToTextboxes") return "dropdownToTextboxes";

  if (field.dataType === "inputToDropdown") return "select";

  if (field.dataType === "textArea") return "textArea";

  if (field.dataType === "string") return "text";

  if (field.dataType === "number") return "number";

  if (field.dataType === "data") return "data";

  if (field.dataType === "date") return "date";

  if (field.dataType === "togglebutton") return "togglelink";

  if (isUndefined(field.component)) return "";

  if (field.component.toLowerCase() === "string") return "text";

  if (field.component.toLowerCase() === "gw-pl-local-date-chooser-group")
    return "date";

  if (field.component.toLowerCase() === "gw-pl-yes-no-group") return "switch";

  if (field.component.toLowerCase() === "boolean") return "checkbox";

  if (
    field.component.toLowerCase() === "ufg-pl-select-group" ||
    field.component.toLowerCase() === "gw-qnb-ho-select-with-other" ||
    field.component.toLowerCase() === "choice"
  )
    return "select";

  return "";
};

export const toString = obj => {
  if (isUndefined(obj)) return "";
  return `${obj}`;
};

export const stripCurrencyChars = val => {
  if (!val) return null;
  return Number(val.replace(/\$|,/g, ""));
};

const isNumberString = n => /^[0-9,.]*$/.test(n);

export const currencyToString = v => {
  if (!v || isNaN(Number(v))) return "0";
  return !!v && /\.{1}/.test(v) ? String(Number(v).toFixed(2)) : String(v);
};

export const stringToCurrency = (v, dec) => {
  if (!v) return "";
  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    style: "currency",
    minimumFractionDigits: dec,
    maximumFractionDigits: dec
  }).format(v);
};

export const toCommaNumber = (v, dec) => {
  if (!v) return "";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: dec
  }).format(v || 0);
};

const cleanSortItem = (a, property) => {
  // if it's not a number, make sure it's lowercase
  // try to Number-ize the comparing value... if it's NaN, then use the string value
  return !!a
    ? isNaN(a[property]) // it's a string
      ? isNumberString(a[property]) // is it a string number?
        ? parseFloat(a[property].split(",").join("")) // it's a string number, remove commas if they exist
        : a[property]?.toLowerCase() // not a number
      : parseFloat(a[property]) // it's a number
    : "";
};

/**
 * properties = [{"field":"name","ascending":true},
 * {"field":"coverageCategoryDisplayName","ascending":false}]
 */
export const sortByProperties = propArr => {
  // usage: arrayOfObjects.sort(sortByProperties([{field:"name",asc:true},
  //                                            {field:"coverageType",asc:false}]))
  return function (a, b) {
    let result = 0;
    for (let i = 0; i < propArr.length; i++) {
      const A = cleanSortItem(a, propArr[i].field);
      const B = cleanSortItem(b, propArr[i].field);

      // if the property isn't available, just return the data
      if (!A || !B) return 0;

      // sort it..the property that comes fast has more weight in sort order.. i.e name higher than coverageType.
      if (!!!propArr[i]?.asc)
        result += (propArr.length - i) * (A > B ? -1 : A < B ? 1 : 0);
      else result += (propArr.length - i) * (A < B ? -1 : A > B ? 1 : 0);
    }
    return result;
  };
};

// usage: arrayOfObjects.sort(sortByProperty("propertyName"))
export const sortByProperty = (property, asc = true) => {
  return sortByProperties([{ field: property, asc }]);
};

export const toHtmlSelectOptions = options => {
  if (options)
    return options
      .map(x => {
        return {
          value: x.value || x.code,
          label: x.label || x.name,
          display: x.label || x.name,
          sortOrder:
            x.optionValue !== undefined ? x.optionValue : x.label || x.name
        };
      })
      .sort(sortByProperty("sortOrder"));
};

export const textTypes = [
  "text",
  "email",
  "hidden",
  "password",
  "range",
  "tel",
  "url",
  "number"
];

export const arrayToOptions = arr => {
  return arr.map(x => {
    return { value: x, label: x, code: x, name: x, optionValue: x };
  });
};

/*
  A function to find a field object in the metadata so we can modify it.

  usage: findFieldObj(details.panels, "path...")
*/
export const findFieldObj = (panels, path) => {
  if (!panels) return {};
  for (const p of panels) {
    for (const r of p.rows) {
      const foundFieldObj = r.fields.find(f => {
        return f.path === path;
      });
      if (foundFieldObj) return foundFieldObj;
    }
  }
  return null;
};

export const newFieldOptions = (
  object,
  fieldName,
  newOptions,
  setFieldToDropdown
) => {
  const fieldObj = findFieldObj(object, fieldName);
  if (fieldObj) {
    fieldObj.options = newOptions.sort(sortByProperty("name"));
    if (setFieldToDropdown) fieldObj.component = "ufg-pl-select-group";
  }
};

export const setFieldProperty = (metadata, fieldName, property, value) => {
  const fieldObj = findFieldObj(metadata, fieldName);
  if (fieldObj) {
    fieldObj[property] = value;
  }
};

// merge field property updates the metadata fieldObjects to a value,
// unless it's an object, then it merges the objects (combines filters)
export const mergeFieldProperty = (metadata, fieldName, property, value) => {
  const fieldObj = findFieldObj(metadata, fieldName);
  if (fieldObj) {
    if (fieldObj[property] && typeof fieldObj[property] === "object") {
      fieldObj[property] = merge(fieldObj[property], value);
    } else {
      fieldObj[property] = value;
    }
  }
};

// function to update the metadata based on an array of fieldObjects
// definitions is an array of objects
/* example definition
 [{
    "baseData.BillingAddress.state": {
      "disabled": true,
      "notClearable": true,
      "forceOptionsToValue": true
    }
  }]
*/
export const updateFieldDefinitions = (metadata, definitions, merge = true) => {
  definitions.forEach(u => {
    const path = Object.keys(u)[0];
    const keys = Object.keys(u[path]);
    keys.forEach(k => {
      if (merge) {
        mergeFieldProperty(metadata, path, k, u[path][k]);
      } else {
        setFieldProperty(metadata, path, k, u[path][k]);
      }
    });
  });
};

export const massageMetadata = (panels, data) => {
  return panels.map((p, pi) => {
    const panelKey = p.key || "panel" + pi;

    return {
      ...p,
      key: panelKey,
      rows: p.rows.map((r, ri) => {
        if (r.jsx) return { jsx: r.jsx, key: panelKey + "row" + ri };
        if (r.fields)
          return {
            ...r,
            key: panelKey + "row" + ri, // react wants a unique row key
            fields: r.fields
              .filter(f => f.hasOwnProperty("label") || f?.jsx)
              .map(f => {
                // allow JSX to be added so regular markup can be added into the array of fields to render
                if (f.jsx)
                  return {
                    jsx: f.jsx,
                    key: `${panelKey}.row.${ri}.field.${f?.fieldKey}`
                  };

                const label = f?.label ? f?.label : " ";
                const path = f.hasOwnProperty("path")
                  ? f.path
                  : f.hasOwnProperty("label")
                  ? f.label.replace(/\s/g, "")
                  : String(Math.random() * 1000);

                // default value is either from quote.json,
                // or one of the possible default value properties in the object
                /*
                TODO:
                This probably should use nullish coalescing operators instead once ReactScripts is updated
                https://github.com/facebook/create-react-app/releases/tag/v3.3.0 and
                https://github.com/tc39/proposal-nullish-coalescing
                */
                const fieldType = getInputType(f);

                const defaultValue =
                  fieldType === "switch"
                    ? _get(data, path, f.defaultValue) // swtiches should not default value to "" - should be boolean
                    : _get(
                        data,
                        path,
                        f.defaultValue || f.chosenTerm || f.selected || ""
                      );

                return {
                  ...f,
                  label,
                  id: path,
                  type: fieldType,
                  options: f.hasOwnProperty("options")
                    ? f.sortOptions === false
                      ? f.options
                      : toHtmlSelectOptions(f.options)
                    : [],
                  defaultValue,
                  disabled: f.disabled !== undefined ? f.disabled : null,
                  onChange: f.onChange || null,
                  // ↓↓↓ a whole bunch of dropdown props
                  multi: f.multi || null
                };
              })
          };
        return null;
      })
    };
  });
};

export const getArrayItemById = (arr, id) => {
  return arr.find(item => item.id === id);
};

export const addUpdateArrayItem = (arr, item, field = "id") => {
  const foundItem = arr.find(x => x[field] === item[field]);
  if (!foundItem) {
    arr.push(item);
    return [...arr];
  }

  const newArr = arr.map(a => {
    return a[field] === item[field] ? item : a;
  });
  return [...newArr];
};

export const locationDisplayName = location => {
  if (!location) return "";

  const displayName = location.address
    ? `${location.address.addressLine1} ${location.address.city}, ${location.address.state} ${location.address.postalCode}`
    : "";

  return location.isPrimary ? `${displayName} (primary)` : displayName;
};

export const clearLocalQuoteManually = () => {
  window.localStorage.setItem(keys.QUOTE_SUPPORT_DATA, null);
  window.localStorage.setItem(keys.CURRENT_QUOTE_DATA, null);
  window.localStorage.setItem(keys.CURRENT_STEP, 1);
};

export const isJsonString = str => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const trimAllObjectValues = obj => {
  if (obj && typeof obj === "object") {
    Object.keys(obj).forEach(key => {
      if (typeof obj[key] === "object") {
        trimAllObjectValues(obj[key]);
      } else if (typeof obj[key] === "string") {
        obj[key] = obj[key].trim();
      }
    });
  }
  return obj;
};

/* 
const myTrim = txt => {
  return txt.trim().replace(/(\r\n+$|\r+$|\n+$|")/gm, "");
};

// COMMENTED OUT UNTIL WE KNOW IF WE NEED IT FOR STEP5

const replaceSpecialChars = txt => {
  txt = String(txt);
  const specialCharacters = [`"`, "'", "&", "#", ";", "/", "\\", ","];
  specialCharacters.forEach(c => {
    txt = txt.replace(c, "");
  });
  return myTrim(txt);
};


export const jsonToCSV = items => {
  let csvContent = "data:text/csv;charset=utf-8,";
  const headerRowsArr = [];
  for (const i in items[0]) {
    if (typeof items[0][i] !== "object") {
      headerRowsArr.push(`"${replaceSpecialChars(i)}"`);
    }
  }
  csvContent = csvContent + headerRowsArr.join(",") + "\r\n"; // add carriage return
  items.map((item, k) => {
    const rowsArr = [];
    for (const i in item) {
      if (typeof item[i] !== "object") {
        rowsArr.push(`"${replaceSpecialChars(item[i])}"`);
      }
    }
    const rows = rowsArr.join(",");
    csvContent = `${csvContent}${rows}\r\n`;
    return k;
  });
  return csvContent;
};

export const csvToJSON = csv => {
  const lines = csv.split("\n");
  const result = [];
  const headers = lines[0].split(",");

  for (let i = 1; i < lines.length; i++) {
    if (!lines[i]) continue;
    const obj = {};
    const currentline = lines[i].split(",");

    for (let j = 0; j < headers.length; j++) {
      const key = headers[j].replace(/(\r|\n|\t|\s|")/g, "");
      const value = replaceSpecialChars(currentline[j]);
      obj[key] = !value || value === "undefined" ? undefined : value;
    }
    result.push(obj);
  }
  return result;
}; */

//clean guid without newline characters.
export const guid = () => {
  return v4().replace(/(\r|\n|\t|\s)/g, "");
};

/*
A shared function that gets city/state based on zip code
... this function sets the city/state fields to be dropdowns
... and it updates the city and state field options with data from the service
... if the service fails, then it changes the city/state fields to inputs
... step1Extras is a special object from step 1 to update the products selection options based on the state that the user selects
*/
export const addressZipLookup = ({
  onChangeFns,
  cityField,
  stateField,
  agencyStates,
  panels,
  setPanels,
  step1Extras
}) => {
  const { field, value, values, errors, updateForm } = onChangeFns;

  const newPanels = [...panels];

  // set the default of values
  // we'll submit this in an update form in the API .finally call
  const newValues = {
    ...values,
    [field]: value,
    [cityField]: "",
    [stateField]: ""
  };
  const newErrors = { ...errors };

  if (RegExp("(^\\d{5}$)|(^\\d{5}-\\d{4}$)").test(value)) {
    setFieldProperty(newPanels, cityField, "isLoading", true);
    setFieldProperty(newPanels, stateField, "isLoading", true);
    setPanels(newPanels);

    // it's a valid zip code... reset the errors -- fixes an issue with copy/pasting of zip code
    delete newErrors[field];

    searchLocaleByZipCodeV2(value)
      .then(result => {
        if (result && result.data && result.data.length) {
          // first check to see if zip is in allowed state list
          if (!agencyStates.includes(result.data[0].state)) {
            newErrors[field] = [OQstrings.error.zip_code_not_in_agent_state];

            // clear selection options
            newFieldOptions(newPanels, cityField, [], true);
            newFieldOptions(newPanels, stateField, [], true);
            setFieldProperty(newPanels, cityField, "disabled", true);
            setFieldProperty(newPanels, stateField, "disabled", true);
          } else {
            newValues[field] = value;
            newValues[cityField] = toTitleCase(result.data[0].city);
            newValues[stateField] = result.data[0].state;

            // prepare the options for city and state
            const newCityOptions = result.data.map(o => {
              return {
                name: toTitleCase(o.city),
                code: toTitleCase(o.city)
              };
            });
            const newStateOptions = result.data.map(o => {
              return {
                name: o.state,
                code: o.state
              };
            });

            // update associated city and state options
            newFieldOptions(newPanels, cityField, newCityOptions, true);
            newFieldOptions(newPanels, stateField, newStateOptions, true);
            setFieldProperty(newPanels, cityField, "disabled", false);
            setFieldProperty(newPanels, stateField, "disabled", false);
          }
        } else {
          // no data returned, empty them out like it was an error
          // 01234 is an example

          // change city and state to input fields
          setFieldProperty(newPanels, cityField, "component", "string");
          // update states options
          newFieldOptions(
            newPanels,
            stateField,
            arrayToOptions(agencyStates),
            true
          );

          setFieldProperty(newPanels, cityField, "disabled", false);
          setFieldProperty(newPanels, stateField, "disabled", false);
        }
      })
      .catch(error => {
        // Error
        if (error.response) {
          // change city and state to input fields
          setFieldProperty(newPanels, cityField, "component", "string");
          // update states options
          newFieldOptions(newPanels, stateField, states, true);
        }
      })
      .finally(() => {
        // turn off loading indicator
        setFieldProperty(newPanels, cityField, "isLoading", false);
        setFieldProperty(newPanels, stateField, "isLoading", false);

        /*

        ---------------------------------
        STEP 1 has some special LOB rules
        ----------------------------------

        The addressZipLookup function updates the city/state.
        When the state is updated, we need to update which products the
        agent has access to and update the filters that control
        which ones can be combined, etc.

        Below: step1StateEntryUpdate returns a set of panels and values
        that are used to update the panel state and the form values

        */
        if (
          !!step1Extras &&
          newValues["baseData.accountHolder.primaryAddress.state"]
        ) {
          // special condidtions for step1 primary state selection
          const st = newValues["baseData.accountHolder.primaryAddress.state"];

          // Aync call (located at ./step1/Step1Panels-UI-update.js) to get the products for the state received from the zip code update
          step1Extras
            .step1StateEntryUpdate({
              st,
              setShowLobModal: step1Extras.setShowLobModal,
              setLobModalData: step1Extras.setLobModalData
            })
            .then(response => {
              const { step1ProductMetadata, step1ProductValues } = response;

              // update the values to make sure all product values are set to false
              _merge(newValues, step1ProductValues);

              // update the product field definitions so only the appropriate products are displayed
              updateFieldDefinitions(
                newPanels,
                step1ProductMetadata,
                false // don't merge, replace instead
              );

              setPanels([...newPanels]);
              updateForm({ values: newValues, errors: newErrors });
            }); // END step1Extras.step1StateEntryUpdate
        } else {
          setPanels([...newPanels]);
          updateForm({ values: newValues, errors: newErrors });
        }
      }); // end of API call
  } else {
    // they didn't input a zip code
    // reset the city state to be disabled
    newFieldOptions(newPanels, cityField, [], true);
    newFieldOptions(newPanels, stateField, [], true);
    setFieldProperty(newPanels, cityField, "disabled", true);
    setFieldProperty(newPanels, stateField, "disabled", true);

    /*
       STEP 1: If it's STEP 1, do something special for the products
      ---> reset them
    */
    if (!!step1Extras) {
      // update the values to make sure all product valus are set to false
      _merge(newValues, step1Extras.resetProducts().resetProductValues);

      // update the product field definitions so the fields are hidden
      updateFieldDefinitions(
        newPanels,
        step1Extras.resetProducts().fieldDefinitionArray,
        false // don't merge, replace instead
      );
    }

    updateForm({ values: newValues, errors: newErrors });
    setPanels([...newPanels]);
  }
};

export const boolToYN = value => {
  return Boolean(value) === true ? "Yes" : "No";
};

export const YNToBool = value => {
  if (!value) return false;
  return String(value).toLowerCase() === "yes";
};

//Remove duplicates from an array by a key that determine uniqueness.
//Or no key required for flat arrays.
/* USAGE:
removeDuplicates(["cat","dog","chicken","dog","horse","cat"]) 
          => ["cat","dog","chicken","horse"]

removeDuplicates([{"name":"cat", "food":"meat"},
                  {"name":"dog", "food":"meat and more"}
                  ,{"name":"chicken","food":"grain"}
                  , {"name":"dog","food":"meat and more"}
                ],"name") 
              => [{"name":"cat", "food":"meat"},
                  {"name":"dog", "food":"meat and more"}
                  ,{"name":"chicken","food":"grain"}
                ]
*/
export const removeDuplicates = (arr, key = null) => {
  if (!key) {
    return arr.filter((v, i) => arr.indexOf(v) === i);
  }
  const innerSet = [...new Set(arr.map(x => x[key]))];
  const unique_array = [];
  innerSet.forEach(a => {
    unique_array.push(arr.find(b => b[key] === a));
  });
  return unique_array;
};

export const toastError = ({
  log,
  displayMessage, // this is optional. If missing, it'll use the generic "Contact Help Desk" message
  toastId = "oq__toast__error",
  onOpen
}) => {
  const message = displayMessage?.props
    ? ReactDOMServer.renderToStaticMarkup(displayMessage)
    : "GenericContactHelpDeskMessage";

  logger({ ...log, displayMessage: message })
    .then(response => {
      // if the log was successful, it returns a 36-char guid
      // ... add it to the toast if we get it

      /**
       * if displayMessage was sent, use it
       * ... otherwise, use the generic "Contact HelpDesk" message
       */
      const ToastMessage = displayMessage ? (
        <>
          <span className="oq__toast-error__message">{displayMessage}</span>
          {response?.data && (
            <small className="oq__toast-error__code">{response.data}</small>
          )}
        </>
      ) : (
        <HelpDeskMessage display="anErrorOccurred" noLinks />
      );
      toast.error(ToastMessage, {
        toastId,
        position: "top-center",
        className: "ufg-toast__error",
        autoClose: false,
        onOpen,
        icon: false
      });
    })
    .catch(() => {
      toast.error(<>{displayMessage}</>, {
        toastId,
        position: "top-center",
        className: "ufg-toast__error",
        autoClose: false,
        onOpen,
        icon: false
      });
    });
};

export const zipCodePattern = /^\d{5}$|^\d{5}-\d{4}$/;

export const zipCodeValidate = value => {
  if (!value) return null;

  if (!zipCodePattern.test(value))
    return "Zip code must be a number with format of 55555 or 55555-1234.";
};

export const addressLineValidate = value => {
  if (!value) return null;
  if (
    !/^[A-Za-z0-9.-]{1,9}[ ]{1}[A-Za-z0-9.]+([A-Za-z0-9. ]|[ ]{1}#\s?[0-9])*$/.test(
      value
    )
  )
    return "Not a valid street address. The address should look like 123 Main St.";
};

const poBoxpattern = new RegExp(
  "\\b[p]*(ost)*\\.*\\s*[o|0]*(ffice)*\\.*\\s*b[o|0]x\\b",
  "i"
);
export const validatePoBox = value => {
  if (!value) return null;

  if (poBoxpattern.test(value)) return "Physical address cannot be a PO Box.";
};

// function to make switch values data boolean
export const transformAnswersToBoolForForm = data => {
  if (!data) return {};

  const newData = {};

  Object.keys(data).forEach(key => {
    // set value for field to not lose the text values
    newData[key] = data[key];

    // set boolean for switches
    if (data[key] === "true") newData[key] = true;
    if (data[key] === "false") newData[key] = false;
  });

  return newData;
};

//by Eamon Nerbonne (from https://eamonnerbonne.github.io/a-vs-an/), Apache 2.0 license
export const AvsAnSimple = (function (root) {
  let dict =
    "2h.#2.a;i;&1.N;*4.a;e;i;o;/9.a;e;h1.o.i;l1./;n1.o.o;r1.e.s1./;01.8;12.1a;01.0;12.8;9;2.31.7;4.5.6.7.8.9.8a;0a.0;1;2;3;4;5;6;7;8;9;11; .22; .–.31; .42; .–.55; .,.h.k.m.62; .k.72; .–.82; .,.92; .–.8;<2.m1.d;o;=1.=1.E;@;A6;A1;A1.S;i1;r1;o.m1;a1;r1; .n1;d1;a1;l1;u1;c1.i1.a1.n;s1;t1;u1;r1;i1;a1;s.t1;h1;l1;e1;t1;e1.s;B2.h2.a1.i1;r1;a.á;o1.r1.d1. ;C3.a1.i1.s1.s.h4.a2.i1.s1;e.o1.i;l1.á;r1.o1.í;u2.i;r1.r1.a;o1.n1.g1.j;D7.a1.o1.q;i2.n1.a1.s;o1.t;u1.a1.l1.c;á1. ;ò;ù;ư;E7;U1;R.b1;o1;l1;i.m1;p1;e1;z.n1;a1;m.s1;p5.a1.c;e;h;o;r;u1.l1;o.w1;i.F11. ;,;.;/;0;1;2;3;4;5;6;71.0.8;9;Ae;B.C.D.F.I2.L.R.K.L.M.N.P.Q.R.S.T.B;C1;M.D;E2.C;I;F1;r.H;I3.A1;T.R1. ;U;J;L3.C;N;P;M;O1. ;P1;..R2.A1. ;S;S;T1;S.U2.,;.;X;Y1;V.c;f1.o.h;σ;G7.e1.r1.n1.e;h1.a3.e;i;o;i1.a1.n1.g;o2.f1. ;t1.t1. ;r1.i1.a;w1.a1.r1.r;ú;Hs. ;&;,;.2;A.I.1;2;3;5;7;B1;P.C;D;F;G;H1;I.I6;C.G.N.P.S1.D;T.K1.9;L;M1;..N;O2. ;V;P;R1;T.S1.F.T;V;e2.i1.r;r1.r1.n;o2.n6;d.e1.s;g.k.o2;l.r1;i1.f;v.u1.r;I3;I2;*.I.n1;d1;e1;p1;e1;n1;d2;e1;n1;c1;i.ê.s1;l1;a1;n1;d1;s.J1.i1.a1.o;Ly. ;,;.;1;2;3;4;8;A3. ;P;X;B;C;D;E2. ;D;F1;T.G;H1.D.I1.R;L;M;N;P;R;S1;m.T;U1. ;V1;C.W1.T;Z;^;a1.o1.i1.g;o1.c1.h1.a1;b.p;u1.s1.h1;o.ộ;M15. ;&;,;.1;A1;.1;S./;1;2;3;4;5;6;7;8;Ai;B.C.D.F.G.J.L.M.N.P.R.S.T.V.W.X.Y.Z.B1;S1;T.C;D;E3.P1;S.W;n;F;G;H;I4. ;5;6;T1;M.K;L;M;N;O1.U;P;Q;R;S;T1;R.U2. ;V;V;X;b1.u1.m;f;h;o2.D1.e.U1;..p1.3;s1.c;Ny. ;+;.1.E.4;7;8;:;A3.A1;F.I;S1.L;B;C;D;E3.A;H;S1. ;F1;U.G;H;I7.C.D1. ;K.L.N.O.S.K;L;M1;M.N2.R;T;P1.O1.V1./1.B;R2;J.T.S1;W.T1;L1.D.U1.S;V;W2.A;O1.H;X;Y3.C1.L;P;U;a1.s1.a1.n;t1.h;v;²;×;O5;N1;E.l1;v.n2;c1.e.e1.i;o1;p.u1;i.P1.h2.i1.a;o2.b2;i.o.i;Q1.i1.n1.g1.x;Rz. ;&;,;.1;J./;1;4;6;A3. ;.;F1;T.B1;R.C;D;E3. ;S1.P;U;F;G;H1.S;I2.A;C1. ;J;K;L1;P.M5;1.2.3.5.6.N;O2.H;T2;A.O.P;Q;R1;F.S4;,...?.T.T;U4;B.M.N.S.V;X;c;f1;M1...h2.A;B;ò;S11. ;&;,;.4.E;M;O;T1..3.B;D;M;1;3;4;5;6;8;9;A3. ;8;S2;E.I.B;C3.A1. ;R2.A.U.T;D;E6. ;5;C3;A.O.R.I1.F.O;U;F3;&.H.O1.S.G1;D.H3.2;3;L;I2. ;S1.O.K2.I.Y.L3;A2. ;.;I1. ;O.M3;A1. ;I.U1.R.N5.A.C3.A.B.C.E.F.O.O5. ;A1.I;E;S1;U.V;P7;A7;A.C.D.M.N.R.S.E1. ;I4;C.D.N.R.L1;O.O.U.Y.Q1. ;R;S1;W.T9.A1. ;C;D;F;I;L;M;S;V;U7.B.L.M.N.P.R.S.V;W1.R;X1.M;h1.i1.g1.a1.o;p1.i1.o1;n.t2.B;i1.c1.i;T4.a2.i2.g1.a.s1.c;v1.e1.s;e1.a1.m1.p;u1.i2.l;r;à;Um..1.N1..1.C;/1.1;11. .21.1;L1.T;M1.N;N4.C1.L;D2. .P.K;R1. .a;b2;a.i.d;g1.l;i1.g.l2;i.y.m;no. ;a1.n.b;c;d;e1;s.f;g;h;i2.d;n;j;k;l;m;n;o;p;q;r;s;t;u;v;w;p;r3;a.e.u1.k;s3. ;h;t1;r.t4.h;n;r;t;x;z;í;W2.P1.:4.A1.F;I2.B;N1.H.O1.V;R1.F1.C2.N.U.i1.k1.i1.E1.l1.i;X7;a.e.h.i.o.u.y.Y3.e1.t1.h;p;s;[5.A;E;I;a;e;_2._1.i;e;`3.a;e;i;a7; .m1;a1;r1. .n1;d2; .ě.p1;r1;t.r1;t1;í.u1;s1;s1;i1. .v1;u1;t.d3.a1.s1. ;e2.m1. ;r1. ;i2.c1.h1. ;e1.s1.e2.m;r;e8;c1;o1;n1;o1;m1;i1;a.e1;w.l1;i1;t1;e1;i.m1;p1;e1;z.n1;t1;e1;n1;d.s2;a1. .t4;a1; .e1; .i1;m1;a1;r.r1;u1.t.u1.p1. ;w.f3. ;M;y1.i;h9. ;,;.;C;a1.u1.t1;b.e2.i1.r1;a.r1.m1.a1.n;o4.m2.a1; .m;n8; .b.d.e3; .d.y.g.i.k.v.r1.s1. ;u1.r;r1. ;t1;t1;p1;:.i6;b1;n.e1;r.n2;f2;l1;u1;ê.o1;a.s1;t1;a1;l1;a.r1; .s1; .u.k1.u1. ;l3.c1.d;s1. ;v1.a;ma. ;,;R;b1.a.e1.i1.n;f;p;t1.a.u1.l1.t1.i1.c1.a1.m1.p1.i;×;n6. ;V;W;d1; .t;×;o8;c2;h1;o.u1;p.d1;d1;y.f1; .g1;g1;i.no. ;';,;/;a;b;c1.o;d;e2.i;r;f;g;i;l;m;n;o;r;s;t;u;w;y;z;–;r1;i1;g1;e.t1;r1.s;u1;i.r3. ;&;f;s9.,;?;R;f2.e.o.i1.c1.h;l1. ;p2.3;i1. ;r1.g;v3.a.e.i.t2.A;S;uc; ...b2.e;l;f.k2.a;i;m1;a1. .n3;a3; .n5.a;c;n;s;t;r1;y.e2; .i.i8.c2.o1.r1.p;u1.m;d1;i1.o;g1.n;l1.l;m1;o.n;s1.s;v1.o1;c.r5;a.e.i.l.o.s3. ;h;u1.r2;e.p3;a.e.i.t2.m;t;v.w1.a;xb. ;';,;.;8;b;k;l;m1;a.t;y1. ;y1.l;{1.a;|1.a;£1.8;À;Á;Ä;Å;Æ;É;Ò;Ó;Ö;Ü;à;á;æ;è;é1;t3.a;o;u;í;ö;ü1; .Ā;ā;ī;İ;Ō;ō;œ;Ω;α;ε;ω;ϵ;е;–2.e;i;ℓ;";
  function fill(node) {
    const kidCount = parseInt(dict, 36) || 0;
    const offset = kidCount && kidCount.toString(36).length;
    node.article = dict[offset] === "." ? "a" : "an";
    dict = dict.substr(1 + offset);
    for (let i = 0; i < kidCount; i++) {
      const kid = (node[dict[0]] = {});
      dict = dict.substr(1);
      fill(kid);
    }
  }
  fill(root);

  return {
    raw: root,
    //Usage example: AvsAnSimple.query("example")
    //example returns: "an"
    query(word) {
      let node = root;
      let sI = 0;
      let result;
      let c;
      do {
        c = word[sI++];
      } while ("\"‘’“”$'-(".indexOf(c) >= 0); //also terminates on end-of-string "undefined".

      while (1) {
        result = node.article || result;
        node = node[c];
        if (!node) return result;
        c = word[sI++] || " ";
      }
    }
  };
})({});

export const hasHNO = supportingData => {
  return (
    !!supportingData?.packages?.hiredNonOwnedAuto?.hiredAuto ||
    !!supportingData?.packages?.hiredNonOwnedAuto?.nonOwnedAuto
  );
};

export const hasAutoWithoutHNO = (quoteData, supportingData) => {
  const autoObj = Object.keys(quoteData?.lobData || []).find(
    p => p === "ca7CommAuto"
  );
  return !!autoObj && !hasHNO(supportingData);
};

export const filterOutAutoIfHNO = (quoteData, supportingData) => {
  return Object.keys(quoteData?.lobData || []).filter(
    p => !(p === "ca7CommAuto" && hasHNO(supportingData))
  );
};

export const statesList =
  "AK,AL,AR,AS,AZ,CA,CO,CT,DC,DE,FL,GA,GU,HI,IA,ID,IL,IN,KS,KY,LA,MA,MD,ME,MI,MN,MO,MP,MS,MT,NC,ND,NE,NH,NJ,NM,NV,NY,OH,OK,OR,PA,PR,RI,SC,SD,TN,TX,UM,UT,VA,VI,VT,WA,WI,WV,WY"
    .split(",")
    .map(s => {
      return {
        label: s,
        value: s
      };
    });

export const sortCoverages = coverages => {
  if (!coverages) return [];

  const array = [
    ...coverages
      .map(c => ({
        ...c,
        sortOrder: getCoverageGroupOrder(c?.coverageCategoryCode) || 0
      }))
      .sort(
        sortByProperties([
          { field: "sortOrder", asc: true },
          { field: "name", asc: true }
        ])
      )
  ];

  const indexArray = array.reduce((acc, obj, index) => {
    acc[obj.coverageName || obj.description] = index;
    return acc;
  }, []);

  return array.sort((a, b) => {
    const b_name = b.coverageName || b.description;
    if (a.showBefore && indexArray[a.showBefore] !== undefined) {
      return indexArray[a.showBefore] - indexArray[b_name] - 1;
    }
    if (a.showAfter && indexArray[a.showAfter] !== undefined) {
      return indexArray[a.showAfter] - indexArray[b_name];
    }
    return 0; // No change in order
  });
};

export const mergeArraysByProperty = ({ target, source, prop }) => {
  source.forEach(sourceElement => {
    const targetElement = target.find(targetElement => {
      return sourceElement[prop] === targetElement[prop];
    });
    if (targetElement) Object.assign(targetElement, sourceElement);
    else target.push(sourceElement);
  });
  return target;
};

export const removeDuplicateQuestions = panels => {
  const addedFields = [];
  const rows = [];
  panels.forEach(p => {
    p.rows.forEach(r => {
      if (!addedFields.find(t => t.path === r.fields[0].path)) {
        addedFields.push(r.fields[0]); //keep track of already added fields
        rows.push({ fields: r.fields });
      }
    });
  });
  return [{ rows }];
};

export const encodeBase64 = data => {
  return Buffer.from(data, "utf8").toString("base64");
};

export const decodeBase64 = data => {
  return Buffer.from(data, "base64").toString("utf8");
};

export const scrollToViewItem = ({ itemId, hash, history }) => {
  const searchStr = hash.replace("#", "");
  if (searchStr) {
    const prodPanel = document.getElementById(itemId.replace("#", ""));
    if (prodPanel) {
      const viewportOffset = prodPanel.getBoundingClientRect();
      const top = viewportOffset?.top;
      const left = viewportOffset?.left;
      window.scrollTo({
        top: top - 100,
        left,
        behavior: "smooth"
      });
      history.replace({ search: "?" }); //reset the search string.
    }
  }
};

export const obfuscate = (str, showlast = 4) => {
  const _str = str || "";
  if (_str.length <= showlast) return "*".repeat(showlast);
  const taillen = _str.length - showlast;
  return "*".repeat(taillen) + _str.slice(-showlast);
};

export const isBeazleyCyberEffectiveDate = ({ supportingData }) => {
  if (
    !supportingData?.beazleyCyberEffectiveDate?.date ||
    !supportingData?.effectiveDate
  )
    return false;
  const beazleyDate = new Date(supportingData?.beazleyCyberEffectiveDate?.date);
  const effDate = formatDate(supportingData?.effectiveDate, "DATE");
  return effDate >= beazleyDate;
};
